import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Switch } from '../ui/switch';
import { Badge } from '../ui/badge';
import { Money } from '../ui/Money';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { Plane, Plus, Trash2, TrendingUp, TrendingDown } from 'lucide-react';
import { TicketRecordDialog } from '../tickets/TicketRecordDialog';

const API = process.env.REACT_APP_BACKEND_URL;

const MONTHS = [
  { value: 1, label: 'January' },
  { value: 2, label: 'February' },
  { value: 3, label: 'March' },
  { value: 4, label: 'April' },
  { value: 5, label: 'May' },
  { value: 6, label: 'June' },
  { value: 7, label: 'July' },
  { value: 8, label: 'August' },
  { value: 9, label: 'September' },
  { value: 10, label: 'October' },
  { value: 11, label: 'November' },
  { value: 12, label: 'December' },
];

const FlightTicketSection = ({ formData, setFormData, handleNestedChange, newTicket, setNewTicket, employeeId }) => {
  const currentYear = new Date().getFullYear();
  const [records, setRecords] = useState([]);
  const [editing, setEditing] = useState(null);
  const token = localStorage.getItem('token');

  const loadRecords = useCallback(async () => {
    if (!employeeId) return;
    try {
      const res = await axios.get(`${API}/api/ticket-tracker/records?employee_id=${employeeId}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setRecords(res.data.records || []);
    } catch (err) {
      /* records are optional in the form */
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [employeeId]);

  useEffect(() => { loadRecords(); }, [loadRecords]);

  const addTicketHistory = () => {
    if (newTicket.year && newTicket.month) {
      setFormData(prev => ({
        ...prev,
        flight_ticket: {
          ...prev.flight_ticket,
          tickets_received: [...prev.flight_ticket.tickets_received, { ...newTicket }]
        }
      }));
      setNewTicket({ year: currentYear, month: 1, notes: '' });
    }
  };

  const removeTicketHistory = (index) => {
    setFormData(prev => ({
      ...prev,
      flight_ticket: {
        ...prev.flight_ticket,
        tickets_received: prev.flight_ticket.tickets_received.filter((_, i) => i !== index)
      }
    }));
  };

  const calculateTicketStats = () => {
    const frequency = formData.flight_ticket.ticket_frequency;
    const tickets = formData.flight_ticket.tickets_received;
    
    if (!formData.join_date) return { eligible: 0, used: tickets.length, balance: -tickets.length };
    
    const joinDate = new Date(formData.join_date);
    const today = new Date();
    const daysSinceJoining = Math.floor((today - joinDate) / (1000 * 60 * 60 * 24));
    const lopDays = formData.lop_days || 0;
    const effectiveDays = daysSinceJoining - lopDays;
    
    const eligibilityDays = frequency === '1_year' ? 365 : 730;
    if (effectiveDays < eligibilityDays) return { eligible: 0, used: tickets.length, balance: -tickets.length };
    
    const frequencyYears = frequency === '1_year' ? 1 : 2;
    const yearsSinceJoining = effectiveDays / 365.0;
    const eligible = Math.floor(yearsSinceJoining / frequencyYears);
    const used = tickets.length;
    
    return { eligible, used, balance: eligible - used };
  };

  const getLastTicketInfo = () => {
    const tickets = formData.flight_ticket.tickets_received;
    if (tickets.length === 0) return 'No tickets received yet';
    
    const sortedTickets = [...tickets].sort((a, b) => {
      if (a.year !== b.year) return b.year - a.year;
      return b.month - a.month;
    });
    
    const last = sortedTickets[0];
    const monthName = MONTHS.find(m => m.value === last.month)?.label || '';
    return `Last: ${monthName} ${last.year}`;
  };

  return (
    <div>
      <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
        <Plane className="h-5 w-5" /> Flight Ticket Entitlement
      </h3>

      <div className="flex items-center justify-between rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 mb-4">
        <div>
          <Label className="font-medium text-gray-900">Ticket Provided by Company</Label>
          <p className="text-xs text-gray-500 mt-0.5">
            When ON, a Ticket Due Reminder is raised on the dashboard whenever this
            employee takes a leave marked "ticket provided".
          </p>
        </div>
        <Switch
          checked={!!formData.flight_ticket.provided_by_company}
          onCheckedChange={(v) => handleNestedChange('flight_ticket', 'provided_by_company', v)}
          data-testid="ticket-provided-by-company"
        />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        <div>
          <Label>Ticket Frequency</Label>
          <Select 
            value={formData.flight_ticket.ticket_frequency} 
            onValueChange={(value) => handleNestedChange('flight_ticket', 'ticket_frequency', value)}
          >
            <SelectTrigger data-testid="ticket-frequency-select">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="1_year">1 Ticket per Year (Manager)</SelectItem>
              <SelectItem value="2_years">1 Ticket every 2 Years (Staff)</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="flex items-end">
          {(() => {
            const stats = calculateTicketStats();
            return (
              <div className="bg-purple-50 p-3 rounded-lg border border-purple-200 w-full" data-testid="ticket-stats-card">
                <div className="grid grid-cols-3 gap-2 text-center">
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Eligible</p>
                    <p className="text-lg font-bold text-blue-600" data-testid="tickets-eligible">{stats.eligible}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Used</p>
                    <p className="text-lg font-bold text-gray-600" data-testid="tickets-used">{stats.used}</p>
                  </div>
                  <div>
                    <p className="text-[10px] text-gray-500 uppercase">Balance</p>
                    <p className={`text-lg font-bold ${stats.balance < 0 ? 'text-red-600' : stats.balance > 0 ? 'text-green-600' : 'text-purple-600'}`} data-testid="tickets-balance">{stats.balance}</p>
                  </div>
                </div>
                <p className="text-xs text-gray-500 text-center mt-1">{getLastTicketInfo()}</p>
              </div>
            );
          })()}
        </div>
      </div>

      <div className="bg-gray-50 p-4 rounded-lg border">
        <Label className="mb-3 block">Tickets Received (History)</Label>
        
        <div className="flex gap-2 mb-3 flex-wrap">
          <Select 
            value={String(newTicket.month)} 
            onValueChange={(value) => setNewTicket(prev => ({ ...prev, month: parseInt(value) }))}
          >
            <SelectTrigger className="w-[140px]" data-testid="ticket-month-select">
              <SelectValue placeholder="Month" />
            </SelectTrigger>
            <SelectContent>
              {MONTHS.map(m => (
                <SelectItem key={m.value} value={String(m.value)}>{m.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Input 
            type="number" 
            placeholder="Year" 
            className="w-[100px]"
            value={newTicket.year}
            onChange={(e) => setNewTicket(prev => ({ ...prev, year: parseInt(e.target.value) || currentYear }))}
            data-testid="ticket-year-input"
          />
          <Input 
            placeholder="Notes (optional)" 
            className="flex-1 min-w-[150px]"
            value={newTicket.notes}
            onChange={(e) => setNewTicket(prev => ({ ...prev, notes: e.target.value }))}
            data-testid="ticket-notes-input"
          />
          <Button type="button" onClick={addTicketHistory} size="sm" data-testid="add-ticket-btn">
            <Plus className="h-4 w-4 mr-1" /> Add
          </Button>
        </div>

        <div className="space-y-2 max-h-[150px] overflow-y-auto">
          {formData.flight_ticket.tickets_received.length === 0 ? (
            <p className="text-sm text-gray-500 italic">No tickets recorded yet</p>
          ) : (
            formData.flight_ticket.tickets_received
              .sort((a, b) => b.year - a.year || b.month - a.month)
              .map((ticket, index) => (
                <div key={index} className="flex items-center justify-between bg-white p-2 rounded border">
                  <span className="text-sm">
                    {MONTHS.find(m => m.value === ticket.month)?.label} {ticket.year}
                    {ticket.notes && <span className="text-gray-500 ml-2">- {ticket.notes}</span>}
                  </span>
                  <Button 
                    type="button" 
                    variant="ghost" 
                    size="sm"
                    onClick={() => removeTicketHistory(index)}
                    data-testid={`remove-ticket-${index}-btn`}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              ))
          )}
        </div>
      </div>

      {employeeId && records.length === 0 && formData.flight_ticket.provided_by_company && (
        <div className="mt-4 rounded-lg border border-dashed border-sky-200 bg-sky-50/50 p-3 text-xs text-gray-500" data-testid="ticket-records-empty">
          No ticket records yet. A record is created automatically when this employee
          takes a leave marked "ticket provided" — it will then appear on the dashboard
          Ticket Due Reminder.
        </div>
      )}

      {employeeId && records.length > 0 && (
        <div className="mt-4 rounded-lg border border-gray-200 p-4" data-testid="ticket-records-section">
          <Label className="mb-3 block flex items-center gap-2">
            <Plane className="h-4 w-4 text-sky-600" /> Ticket Tracker (Leave-driven, Company-paid)
          </Label>
          <div className="space-y-2">
            {records.map((r) => {
              const diff = r.fare?.difference;
              return (
                <div key={r.id} className="flex items-center justify-between gap-3 bg-white p-2.5 rounded border" data-testid={`ticket-record-row-${r.id}`}>
                  <div className="min-w-0">
                    <p className="text-sm font-medium text-gray-800">
                      Leave {r.leave_start_date}
                      <Badge className={`ml-2 text-[10px] border-0 ${r.status === 'issued' ? 'bg-emerald-100 text-emerald-700' : 'bg-amber-100 text-amber-700'}`}>
                        {r.status === 'issued' ? 'Issued' : 'Pending'}
                      </Badge>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {r.actual_issue_date ? `Issued ${r.actual_issue_date}` : (r.issue_due_date ? `Due ${r.issue_due_date}` : 'Not yet issued')}
                      {diff != null && (
                        <span className={`ml-2 inline-flex items-center gap-0.5 font-semibold ${diff > 0 ? 'text-emerald-600' : diff < 0 ? 'text-rose-600' : 'text-gray-500'}`}>
                          {diff > 0 ? <TrendingDown className="h-3 w-3" /> : diff < 0 ? <TrendingUp className="h-3 w-3" /> : null}
                          {diff > 0 ? '+' : diff < 0 ? '-' : ''}<Money value={Math.abs(diff)} digits={0} />
                        </span>
                      )}
                    </p>
                  </div>
                  <Button type="button" size="sm" variant="outline" onClick={() => setEditing(r)} data-testid={`edit-ticket-record-${r.id}`}>
                    {r.status === 'issued' ? 'Edit' : 'Record'}
                  </Button>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <TicketRecordDialog
        record={editing}
        open={!!editing}
        onOpenChange={(o) => { if (!o) setEditing(null); }}
        onSaved={() => { setEditing(null); loadRecords(); }}
      />
    </div>
  );
};

export default FlightTicketSection;
